interface ArcadeCardProps {
  best: number | null; // this week's server-verified ranked best (board.viewer.score) — null if none yet
  rank: number | null; // this week's global rank (board.viewer.rank) — null alongside best
  // ranked runs left this week; 0 flips the value line to the barista script.
  // null means "not loaded yet" — NOT zero, and NOT a guessed nonzero either:
  // see the null branch below for why neither claim is safe before the real
  // number arrives.
  tries: number | null;
  onOpen: () => void; // HOME → ArcadePanel overlay
}

// Same idiom as StampCard: full-width button, pixel-corners, coral eyebrow,
// bold value line, right-hand slot for a secondary number. The value line's
// three states — no tries, no score, has a score — are mutually exclusive
// and checked in that order, since a stamped-out player with zero tries
// still gets told to get stamped rather than "no score yet" (the copy that
// matters is "why can't I play", not "what's my score").
//
// `tries === null` (not yet loaded) is handled separately from `tries === 0`
// (loaded, genuinely zero) — collapsing them was the bug this shape fixes.
// `tries === 0` is a strict equality check, so `null` never satisfies it and
// never trips the barista line; the right-hand slot gets its own explicit
// placeholder rather than falling through to `null RUNS`. Committing to
// EITHER "you have tries" (the old hardcoded default) or "you have none"
// before the real number is in is a false claim either way — one just
// happens to send a stamped, active player who's fine to play right now
// toward the till for no reason, which is worse than one wasted tap into a
// panel that self-corrects.
export default function ArcadeCard({ best, rank, tries, onOpen }: ArcadeCardProps) {
  return (
    // 2px ink trim. `pixel-corners` is a clip-path, so a CSS border would be
    // sliced at every step — the trim has to come from an ink-filled wrapper
    // carrying the same clip (same technique as TabBar).
    <div className="w-full pixel-corners bg-primary p-[2px]">
    <button
      type="button"
      aria-label="arcade"
      onClick={onOpen}
      className="w-full bg-white pixel-corners px-[16px] py-[8px] flex items-center justify-between gap-3"
    >
      <span className="flex items-baseline gap-2.5 min-w-0">
        <span className="font-heading text-[8px] text-coral shrink-0">BEAN SNAKE</span>
        <span className="font-grotesk font-bold text-[13px] text-primary truncate">
          {tries === 0 ? (
            'Get stamped to play ranked'
          ) : best === null ? (
            'No score yet'
          ) : (
            <>
              <span>{best}</span> best · #{rank}
            </>
          )}
        </span>
      </span>
      <span className="font-heading text-[8px] text-body-grey shrink-0">
        {tries === null ? '···' : `${tries} RUNS`}
      </span>
    </button>
    </div>
  );
}
